import React, { useMemo } from 'react';
import { SurchargeConfigForm, type SurchargeSectionConfig } from '@/components/config_forms/surcharge/SurchargeConfigForm';
import { ClientHolidayConfig } from './ClientHolidayConfig';
import { ClientMealBreakConfig } from './ClientMealBreakConfig';
import { ClientOvernightConfig } from './ClientOvernightConfig';
import { ClientShortTurnConfig } from './ClientShortTurnConfig';
import type { ClientFormData } from '../new_client/utilities/formDataUtils';
import type { ValidationError } from '@/utils/validation/types';
import { Clock, Calendar, Utensils, Moon } from 'lucide-react';

interface ClientSurchargeConfigProps {
  formData: ClientFormData;
  handleChange: (field: keyof ClientFormData, value: ClientFormData[keyof ClientFormData]) => void;
  handleSwitchChange: (field: keyof ClientFormData, checked: boolean) => void;
  onFieldBlur?: (field: keyof ClientFormData) => void;
  isLoading?: boolean;
  errors?: ValidationError[];
  touchedFields?: Set<string>;
  granularCustomizedFields: Record<string, boolean>;
  granularCustomized: {
    holiday: boolean;
    mealBreak: boolean;
    shortTurn: boolean;
    overnight: boolean;
  };
  onRevertSection: (section: 'holiday' | 'mealBreak' | 'shortTurn' | 'overnight') => void;
}

export const ClientSurchargeConfig: React.FC<ClientSurchargeConfigProps> = ({
  formData,
  handleChange,
  handleSwitchChange,
  onFieldBlur,
  isLoading,
  errors,
  touchedFields,
  granularCustomizedFields,
  granularCustomized,
  onRevertSection,
}) => {
  const commonProps = useMemo(() => ({
    formData,
    handleChange,
    handleSwitchChange,
    onFieldBlur,
    isLoading,
    errors,
    touchedFields,
    granularCustomizedFields,
  }), [formData, handleChange, handleSwitchChange, onFieldBlur, isLoading, errors, touchedFields, granularCustomizedFields]);

  // Check if any surcharge section is customized
  const hasAnyCustomization = granularCustomized.holiday || granularCustomized.mealBreak || granularCustomized.shortTurn || granularCustomized.overnight;
  const customizationCount = Object.values(granularCustomized).filter(Boolean).length;

  const handleRevertAll = () => {
    // Revert all customized sections
    if (granularCustomized.holiday) onRevertSection('holiday');
    if (granularCustomized.mealBreak) onRevertSection('mealBreak');
    if (granularCustomized.shortTurn) onRevertSection('shortTurn');
    if (granularCustomized.overnight) onRevertSection('overnight');
  };

  const sections: SurchargeSectionConfig[] = [
    {
      key: 'shortTurn',
      title: 'Short Turn Surcharge',
      icon: Clock,
      component: ClientShortTurnConfig as unknown as React.ComponentType<Record<string, unknown>>,
      isCustomized: granularCustomized.shortTurn,
      description: "Charge extra when rest between consecutive work days falls below the minimum.",
      isChecked: !!formData.uses_short_turn,
      onCheckedChange: (checked) => handleSwitchChange('uses_short_turn', checked),
    },
    {
      key: 'mealBreak',
      title: 'Meal Break Surcharge',
      icon: Utensils,
      component: ClientMealBreakConfig as unknown as React.ComponentType<Record<string, unknown>>,
      isCustomized: granularCustomized.mealBreak,
      description: "Enforce surcharges for missed or late meal breaks.",
      isChecked: !!formData.apply_meal_surcharge,
      onCheckedChange: (checked) => handleSwitchChange('apply_meal_surcharge', checked),
    },
    {
      key: 'holiday',
      title: 'Holiday Premium',
      icon: Calendar,
      component: ClientHolidayConfig as unknown as React.ComponentType<Record<string, unknown>>,
      isCustomized: granularCustomized.holiday,
      description: "Enable special rates for work performed on selected holidays.",
      isChecked: !!formData.apply_holiday_premium,
      onCheckedChange: (checked) => handleSwitchChange('apply_holiday_premium', checked),
    },
    {
      key: 'overnight',
      title: 'Overnight Premium',
      icon: Moon,
      component: ClientOvernightConfig as unknown as React.ComponentType<Record<string, unknown>>,
      isCustomized: granularCustomized.overnight,
      description: "Apply premium compensation for hours worked during a late-night window.",
      isChecked: !!formData.apply_overnight_premium,
      onCheckedChange: (checked) => handleSwitchChange('apply_overnight_premium', checked),
    },
  ];

  return (
    <SurchargeConfigForm
      title="Client Surcharge Rules"
      description="Override your default surcharge configurations for this specific client. Customized rules will take precedence over your profile defaults."
      sections={sections}
      hasAnyCustomization={hasAnyCustomization}
      customizationCount={customizationCount}
      onRevertSection={onRevertSection as (sectionKey: string) => void}
      onRevertAll={handleRevertAll}
      commonProps={commonProps}
      variant="client"
    />
  );
};
